import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Eye } from 'lucide-react';
import { toast } from 'sonner';

const StudentsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [seniors, setSeniors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const canAssignExec = ['director', 'senior_executive'].includes(user?.role);
  const canAssignSenior = ['director', 'admin'].includes(user?.role);

  const fetchStudents = useCallback(async (q) => {
    try {
      const { data } = await axios.get(`${API}/staff/students`, { params: q ? { search: q } : {} });
      setStudents(data);
    } catch (e) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents('');
    if (canAssignExec) {
      axios.get(`${API}/staff/executives`).then((r) => setExecutives(r.data)).catch(() => {});
    }
    if (canAssignSenior) {
      axios.get(`${API}/staff/senior-executives`).then((r) => setSeniors(r.data)).catch(() => {});
    }
  }, [fetchStudents, canAssignExec, canAssignSenior]);

  useEffect(() => {
    const t = setTimeout(() => fetchStudents(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchStudents]);

  const handleAssign = async (studentId, executiveId) => {
    try {
      await axios.post(`${API}/staff/assign-student`, {
        student_id: studentId,
        executive_id: executiveId === 'unassigned' ? null : executiveId,
      });
      toast.success('Executive assigned');
      fetchStudents(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleAssignSenior = async (studentId, seniorId) => {
    try {
      await axios.post(`${API}/staff/assign-senior`, {
        student_id: studentId,
        senior_executive_id: seniorId === 'unassigned' ? null : seniorId,
      });
      toast.success('Senior Executive assigned');
      fetchStudents(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const subtitle = {
    executive: 'Students assigned to you',
    senior_executive: 'Students handed to you and your team',
    admin: 'All students — assign each one to a Senior Executive',
  }[user?.role] || 'All students';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="students-list-page">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Students</h1>
        <p className="text-lg text-slate-600">{subtitle}</p>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="students-search-input"
        />
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle data-testid="students-count">{students.length} student(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : students.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No students found.</p>
          ) : (
            <div className="space-y-2">
              {students.map((s) => (
                <div
                  key={s.id}
                  className="flex flex-col md:flex-row md:items-center gap-3 justify-between py-3 px-3 rounded-lg hover:bg-slate-50 border border-slate-100"
                  data-testid={`student-row-${s.id}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{s.name}</div>
                    <div className="text-sm text-slate-500">{s.email}</div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                      ₹{(s.total_paid || 0).toFixed(0)} paid
                    </Badge>
                    {s.assigned_senior_executive_name && (
                      <Badge variant="secondary" className="bg-violet-50 text-violet-700" data-testid={`student-senior-badge-${s.id}`}>
                        Sr: {s.assigned_senior_executive_name}
                      </Badge>
                    )}
                    {s.assigned_executive_name ? (
                      <Badge variant="secondary" className="bg-indigo-50 text-indigo-700" data-testid={`student-exec-badge-${s.id}`}>
                        Exec: {s.assigned_executive_name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500" data-testid={`student-unassigned-badge-${s.id}`}>
                        {s.assigned_senior_executive_name ? 'Awaiting Executive' : 'Unassigned'}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {canAssignSenior && (
                      <Select
                        value={s.assigned_senior_executive_id || 'unassigned'}
                        onValueChange={(v) => handleAssignSenior(s.id, v)}
                      >
                        <SelectTrigger className="w-44" data-testid={`assign-senior-select-${s.id}`}>
                          <SelectValue placeholder="Senior Executive" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">No Senior Exec</SelectItem>
                          {seniors.map((se) => (
                            <SelectItem key={se.id} value={se.id}>{se.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {canAssignExec && (
                      <Select
                        value={s.assigned_executive_id || 'unassigned'}
                        onValueChange={(v) => handleAssign(s.id, v)}
                      >
                        <SelectTrigger className="w-40" data-testid={`assign-select-${s.id}`}>
                          <SelectValue placeholder="Assign" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {executives.map((ex) => (
                            <SelectItem key={ex.id} value={ex.id}>{ex.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      size="sm"
                      onClick={() => navigate(`/staff/students/${s.id}`)}
                      data-testid={`view-student-${s.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentsList;
